// services/lead-generation/data-sources/CompaniesHouseAPI.js

export class CompaniesHouseAPI {
  constructor() {
    // Use backend proxy instead of direct API calls
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    this.rateLimitDelay = 300; // 300ms delay between requests
    this.lastRequestTime = 0;
    
    console.log('CompaniesHouseAPI initialized with baseURL:', this.baseURL);
  }

  /**
   * Respect rate limits - 600 requests per 5 minutes
   */
  async respectRateLimit() {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.rateLimitDelay) {
      await new Promise(resolve => 
        setTimeout(resolve, this.rateLimitDelay - timeSinceLastRequest)
      );
    }
    
    this.lastRequestTime = Date.now();
  }

  /**
   * Make request to backend proxy (no auth needed - backend handles it)
   */
  async makeRequest(endpoint) {
    await this.respectRateLimit();
    
    // Debug logging
    console.log('Making request to backend proxy:', `${this.baseURL}${endpoint}`);
    
    const response = await fetch(`${this.baseURL}${endpoint}`);

    console.log('Backend response status:', response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('Backend API Error:', errorData);
      throw new Error(`Backend API error: ${response.status} - ${errorData.message || errorData.error}`);
    }

    const data = await response.json();
    console.log('Backend response data keys:', Object.keys(data));
    
    return data;
  }

  /**
   * Search for companies by name, location, or other criteria
   * @param {Object} searchParams - Search parameters
   * @returns {Promise<Array>} Array of company results
   */
  async searchCompanies(searchParams) {
    const { query, items_per_page = 20, start_index = 0 } = searchParams;
    
    try {
      const endpoint = `/companies/search?q=${encodeURIComponent(query)}&items_per_page=${items_per_page}&start_index=${start_index}`;
      
      console.log('Searching companies with endpoint:', endpoint);
      const data = await this.makeRequest(endpoint);
      
      console.log('Raw API response:', data);
      console.log('Items found:', data.items?.length || 0);
      
      if (!data.items || data.items.length === 0) {
        console.log('No companies found in response');
        return [];
      }
      
      const transformedCompanies = data.items.map(company => this.transformCompanyData(company));
      console.log('Transformed companies:', transformedCompanies.length);
      
      return transformedCompanies;
    } catch (error) {
      console.error('Companies House search error:', error);
      throw new Error(`Failed to search companies: ${error.message}`);
    }
  }

  /**
   * Get detailed company information by company number
   * @param {string} companyNumber - UK company registration number
   * @returns {Promise<Object>} Detailed company data
   */
  async getCompanyDetails(companyNumber) {
    try {
      const endpoint = `/companies/${companyNumber}`;
      const companyData = await this.makeRequest(endpoint);
      
      return this.transformCompanyData(companyData);
    } catch (error) {
      console.error(`Error getting company details for ${companyNumber}:`, error);
      throw new Error(`Failed to get company details: ${error.message}`);
    }
  }

  /**
   * Transform Companies House data to our standardized format
   * @param {Object} companyData - Raw Companies House company data
   * @returns {Object} Standardized company data
   */
  transformCompanyData(companyData) {
    console.log('Transforming company data for:', companyData.company_name);
    
    // Extract SIC codes and map to our industry classification
    const sicCodes = companyData.sic_codes || [];
    const primarySicCode = sicCodes[0] || null;
    const industry = this.mapSicCodeToIndustry(primarySicCode);

    // Estimate company size from available data
    const { employees, turnover, balanceSheet } = this.estimateCompanySize(companyData);

    // Determine company status and active state
    const isActive = companyData.company_status === 'active';
    const canBeContacted = companyData.can_file === true;

    const transformedData = {
      id: `ch_${companyData.company_number}`,
      companyNumber: companyData.company_number,
      name: companyData.company_name,
      
      // Location information
      location: this.formatAddress(companyData.address || companyData.registered_office_address),
      address: companyData.address || companyData.registered_office_address,
      
      // Business classification
      sicCodes,
      primarySicCode,
      industry,
      
      // Company metrics (estimated)
      employees,
      turnover,
      balanceSheet,
      
      // Company status
      isActive,
      canBeContacted,
      companyStatus: companyData.company_status,
      companyType: companyData.company_type || companyData.type,
      
      // Dates
      incorporationDate: companyData.date_of_creation,
      lastAccountsDate: companyData.accounts?.last_accounts?.made_up_to,
      nextAccountsDue: companyData.accounts?.next_due,
      
      // Additional business data
      isListed: this.checkIfListed(companyData),
      hasSubsidiaries: companyData.has_charges || false,
      
      // Data source attribution
      dataSource: 'Companies House',
      dataFreshness: new Date().toISOString(),
      
      // Raw data for debugging
      _raw: companyData
    };
    
    console.log('Transformed company:', transformedData.name, 'ID:', transformedData.id);
    return transformedData;
  }

  /**
   * Format address from Companies House address object
   */
  formatAddress(address) {
    if (!address) return 'Location not available';
    
    const parts = [
      address.address_line_1,
      address.address_line_2,
      address.locality,
      address.region,
      address.postal_code,
      address.country
    ].filter(Boolean);
    
    return parts.join(', ');
  }

  /**
   * Map SIC code to industry category
   * @param {string} sicCode - Standard Industry Classification code
   * @returns {string} Industry category
   */
  mapSicCodeToIndustry(sicCode) {
    if (!sicCode) return 'Professional Services';
    
    const sicPrefix = sicCode.substring(0, 2);
    const sicMapping = {
      '01': 'Agriculture, forestry and fishing',
      '02': 'Agriculture, forestry and fishing', 
      '03': 'Agriculture, forestry and fishing',
      '05': 'Mining and quarrying',
      '06': 'Mining and quarrying',
      '07': 'Mining and quarrying',
      '08': 'Mining and quarrying',
      '09': 'Mining and quarrying',
      '10': 'Manufacturing',
      '11': 'Manufacturing',
      '12': 'Manufacturing',
      '13': 'Manufacturing',
      '14': 'Manufacturing',
      '15': 'Manufacturing',
      '16': 'Manufacturing',
      '17': 'Manufacturing',
      '18': 'Manufacturing',
      '19': 'Manufacturing',
      '20': 'Manufacturing',
      '21': 'Manufacturing',
      '22': 'Manufacturing',
      '23': 'Manufacturing',
      '24': 'Manufacturing',
      '25': 'Manufacturing',
      '26': 'Manufacturing',
      '27': 'Manufacturing',
      '28': 'Manufacturing',
      '29': 'Manufacturing',
      '30': 'Manufacturing',
      '31': 'Manufacturing',
      '32': 'Manufacturing',
      '33': 'Manufacturing',
      '35': 'Electricity, gas, steam and air conditioning supply',
      '36': 'Water supply; sewerage, waste management',
      '37': 'Water supply; sewerage, waste management',
      '38': 'Water supply; sewerage, waste management',
      '39': 'Water supply; sewerage, waste management',
      '41': 'Construction',
      '42': 'Construction',
      '43': 'Construction',
      '45': 'Wholesale and retail trade; repair of motor vehicles',
      '46': 'Wholesale and retail trade; repair of motor vehicles',
      '47': 'Wholesale and retail trade; repair of motor vehicles',
      '49': 'Transportation and storage',
      '50': 'Transportation and storage',
      '51': 'Transportation and storage',
      '52': 'Transportation and storage',
      '53': 'Transportation and storage',
      '55': 'Accommodation and food service activities',
      '56': 'Accommodation and food service activities',
      '58': 'Information and communication',
      '59': 'Information and communication',
      '60': 'Information and communication',
      '61': 'Information and communication',
      '62': 'Information and communication',
      '63': 'Information and communication',
      '64': 'Financial and insurance activities',
      '65': 'Financial and insurance activities',
      '66': 'Financial and insurance activities',
      '68': 'Real estate activities',
      '69': 'Professional, scientific and technical activities',
      '70': 'Professional, scientific and technical activities',
      '71': 'Professional, scientific and technical activities',
      '72': 'Professional, scientific and technical activities',
      '73': 'Professional, scientific and technical activities',
      '74': 'Professional, scientific and technical activities',
      '75': 'Professional, scientific and technical activities',
      '77': 'Administrative and support service activities',
      '78': 'Administrative and support service activities',
      '79': 'Administrative and support service activities',
      '80': 'Administrative and support service activities',
      '81': 'Administrative and support service activities',
      '82': 'Administrative and support service activities',
      '84': 'Public administration and defence',
      '85': 'Education',
      '86': 'Human health and social work activities',
      '87': 'Human health and social work activities',
      '88': 'Human health and social work activities',
      '90': 'Arts, entertainment and recreation',
      '91': 'Arts, entertainment and recreation',
      '92': 'Arts, entertainment and recreation',
      '93': 'Arts, entertainment and recreation',
      '94': 'Other service activities',
      '95': 'Other service activities',
      '96': 'Other service activities'
    };

    return sicMapping[sicPrefix] || 'Other services';
  }

  /**
   * Estimate company size from available Companies House data
   * Note: Companies House doesn't provide exact employee/revenue figures
   * This provides educated estimates based on available data
   */
  estimateCompanySize(companyData) {
    let employees = null;
    let turnover = null;
    let balanceSheet = null;

    // Try to extract from accounts data if available
    if (companyData.accounts) {
      // Some companies file abbreviated accounts with size indicators
      const accountsCategory = companyData.accounts.accounting_reference_date;
      
      // Estimate based on company type and SIC codes
      const companyType = companyData.type || companyData.company_type;
      const sicCodes = companyData.sic_codes || [];
      
      // Basic estimation logic (this would be enhanced with more data sources)
      if (companyType === 'plc') {
        // Public Limited Companies tend to be larger
        employees = this.estimateRange(250, 2000);
        turnover = this.estimateRange(50000000, 500000000);
        balanceSheet = this.estimateRange(25000000, 250000000);
      } else if (companyType === 'ltd') {
        // Private Limited Companies - wide range
        employees = this.estimateRange(10, 500);
        turnover = this.estimateRange(1000000, 100000000);
        balanceSheet = this.estimateRange(500000, 50000000);
      } else {
        // Default estimates for other types
        employees = this.estimateRange(5, 100);
        turnover = this.estimateRange(500000, 10000000);
        balanceSheet = this.estimateRange(250000, 5000000);
      }
    } else {
      // Default estimates when no accounts data available
      employees = this.estimateRange(5, 50);
      turnover = this.estimateRange(500000, 5000000);
      balanceSheet = this.estimateRange(250000, 2500000);
    }

    console.log(`Estimated size for ${companyData.company_name}: ${employees} employees, ${formatCurrency(turnover)} turnover`);
    return { employees, turnover, balanceSheet };
  }

  /**
   * Generate a random number within a range for estimation
   */
  estimateRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Check if company is likely to be listed (rough heuristic)
   */
  checkIfListed(companyData) {
    const companyType = companyData.company_type || companyData.type;
    return companyType === 'plc' || companyType === 'public-limited-company';
  }

  /**
   * Health check for Companies House API via backend
   * @returns {Promise<Object>} API health status
   */
  async healthCheck() {
    try {
      const data = await this.makeRequest('/companies/health');
      return data;
    } catch (error) {
      return {
        status: 'unhealthy',
        timestamp: new Date().toISOString(),
        error: error.message
      };
    }
  }
}

// Helper function for currency formatting
function formatCurrency(amount) {
  if (!amount) return 'N/A';
  if (amount >= 1000000) return `€${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `€${(amount / 1000).toFixed(0)}K`;
  return `€${amount}`;
}

// Export singleton instance (no API key needed - backend handles auth)
export const companiesHouseAPI = new CompaniesHouseAPI();